// src/app/app-routing.module.ts

import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Componentes principales
import { HomeComponent } from './components/home/home.component';
import { HomeQaHarnessComponent } from './components/home/home-qa-harness/home-qa-harness.component';
import { HomePublicComponent } from './components/home-public/home-public.component';
import { DocumentsTabsComponent } from './components/documents/documents-tabs.component';
import { KnowledgeHubComponent } from './components/knowledge-hub/knowledge-hub.component';
import { AboutInnovadocIaComponent } from './components/about-innovadoc/about-innovadoc-ia.component';
import { PrivacyComponent } from './components/public/privacy.component';
import { TermsComponent } from './components/public/terms.component';
import { GoogleCallbackComponent } from './components/settings/google-callback/google-callback.component';
import { MicrosoftCallbackComponent } from './components/microsoft-callback/microsoft-callback.component';

// Componentes de autenticación
import { LoginComponent } from './components/auth/login/login.component';
import { SignupComponent } from './components/auth/signup/signup.component';
import { WizardComponent } from './components/auth/wizard/wizard.component';
import { InviteWizardComponent } from './components/auth/invite-wizard/invite-wizard.component';

// Componentes nuevos / protegidos
import { ChatComponent } from './components/chat/chat.component';
import { ProfileComponent } from './components/profile/profile.component';

// Guards e interceptores
import { AuthGuard } from './guards/auth.guard';
import { OnboardingGuard } from './guards/onboarding.guard';
import { AdminGuard } from './guards/admin.guard';
import { DocGenerationFeatureGuard } from './guards/doc-generation-feature.guard';
import { KnowledgeHubFeatureGuard } from './guards/knowledge-hub-feature.guard';
import { ChangeDetectionFeatureGuard } from './guards/change-detection-feature.guard';
import { KnowledgeAlertsFeatureGuard } from './guards/knowledge-alerts-feature.guard';
import { environment } from '../environments/environment';
// import { AuthInterceptor } from './interceptors/auth.interceptor';

export const routes: Routes = [
  // Rutas públicas
  { path: '', component: HomePublicComponent, pathMatch: 'full' },
  { path: 'login', component: LoginComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'onboarding', component: InviteWizardComponent },
  // P2-4: ruta huérfana (0 routerLink); redirige al canónico. Módulo se retira en release posterior.
  { path: 'about', redirectTo: 'about-innovadoc-ia', pathMatch: 'full' },
  { path: 'about-innovadoc-ia', component: AboutInnovadocIaComponent },
  ...(environment.homeIaQaHarnessEnabled ? [{ path: 'home-qa', component: HomeQaHarnessComponent }] : []),
  // Callback OAuth (público, sin guards)
  { path: 'callback', component: GoogleCallbackComponent },
  { path: 'oauth/microsoft/callback', component: MicrosoftCallbackComponent },
  { path: 'privacy', component: PrivacyComponent },
  { path: 'terms', component: TermsComponent },

  // Rutas protegidas por AuthGuard
  {
    path: '',
    canActivate: [AuthGuard, OnboardingGuard],
    canActivateChild: [OnboardingGuard],
    canMatch: [OnboardingGuard],
    children: [
      { path: 'home', component: HomeComponent },
      { path: 'home-ia/dashboard', redirectTo: 'home', pathMatch: 'full' },
      { path: 'chat', component: ChatComponent },
      // Knowledge Hub: shell con pestañas que agrupa Generar / Conocimiento / Cambios / Alertas.
      // Cada hija sigue siendo lazy y conserva su feature guard.
      {
        path: 'knowledge-hub',
        component: KnowledgeHubComponent,
        children: [
          {
            path: 'generate',
            canMatch: [DocGenerationFeatureGuard],
            canActivate: [DocGenerationFeatureGuard],
            loadChildren: () => import('./components/generate/generate-page.module').then(m => m.GeneratePageModule)
          },
          {
            path: 'knowledge',
            canMatch: [KnowledgeHubFeatureGuard],
            canActivate: [KnowledgeHubFeatureGuard],
            loadChildren: () => import('./components/knowledge/knowledge-page.module').then(m => m.KnowledgePageModule)
          },
          {
            path: 'changes',
            canMatch: [ChangeDetectionFeatureGuard],
            canActivate: [ChangeDetectionFeatureGuard],
            loadChildren: () => import('./components/changes/changes-page.module').then(m => m.ChangesPageModule)
          },
          {
            path: 'alerts',
            canMatch: [KnowledgeAlertsFeatureGuard],
            canActivate: [KnowledgeAlertsFeatureGuard],
            loadChildren: () => import('./components/alerts/alerts-page.module').then(m => m.AlertsPageModule)
          }
          // Sin default estático: KnowledgeHubComponent redirige a la primera pestaña habilitada.
        ]
      },
      // Compatibilidad: las rutas viejas redirigen al Knowledge Hub conservando subrutas
      // (prefix match) para no romper deep-links internos como /generate/documentos-generados
      // ni /knowledge/entidades. Los guards siguen aplicando en las hijas del hub.
      { path: 'generate', redirectTo: 'knowledge-hub/generate' },
      { path: 'knowledge', redirectTo: 'knowledge-hub/knowledge' },
      { path: 'changes', redirectTo: 'knowledge-hub/changes' },
      { path: 'alerts', redirectTo: 'knowledge-hub/alerts' },
      // P2-4: ruta huérfana (0 routerLink); redirige al canónico. Módulo se retira en release posterior.
      { path: 'dashboards/knowledge', redirectTo: 'dashboard', pathMatch: 'full' },
      // P2-4: ruta huérfana (0 routerLink); redirige al canónico. Módulo se retira en release posterior.
      { path: 'upload', redirectTo: 'documents', pathMatch: 'full' },
      { path: 'documents', component: DocumentsTabsComponent },
      { path: 'profile', component: ProfileComponent },
      {
        // Destino unificado: fusiona la antigua /usage (primaria) y /dashboard (secundaria, admin).
        path: 'plan',
        loadChildren: () =>
          import('./features/plans/plan.module').then(m => m.PlanModule)
      },
      // P2-4: /usage se conserva como redirección al canónico /plan.
      { path: 'usage', redirectTo: 'plan', pathMatch: 'full' },
      {
        path: 'payments/wompi/result',
        loadChildren: () =>
          import('./features/plans/payment-result.module').then(m => m.PaymentResultModule)
      },
      {
        path: 'preferencias',
        loadChildren: () => import('./components/settings/settings-page.module').then(m => m.SettingsPageModule)
      },
      { path: 'settings', redirectTo: 'preferencias', pathMatch: 'full' },
      {
        path: 'tasks',
        loadChildren: () => import('./components/tasks/tasks-page.module').then(m => m.TasksPageModule)
      },

      // wizard tras signup
      { path: 'wizard', component: WizardComponent },

      // rutas solo para admins
      {
        path: 'admin',
        canActivate: [AdminGuard],
        loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule)
      },
      // P2-4: /dashboard se fusiona en /plan (pestaña secundaria "Detalle", protegida por AdminGuard).
      { path: 'dashboard', redirectTo: 'plan', pathMatch: 'full' }
    ]
  },

  // fallback
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes)
  ],
  exports: [RouterModule],
  providers: []
})
export class AppRoutingModule { }
